import React from "react";

const CustomerTermsContent: React.FC = () => (
  <>
      <p>
        These User Terms and Conditions, together with any policies, guidelines, payment terms,
        privacy-related disclosures, and other documents expressly incorporated by reference, form a
        legally binding agreement between Pheefee Technologies Inc. (&ldquo;Pheefee,&rdquo;
        &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) and the
        individual accepting these Terms to access or use the platform to review, book, or receive
        services from independent trainers (&ldquo;User&rdquo;, &ldquo;Customer&rdquo;,
        &ldquo;you,&rdquo; or &ldquo;your&rdquo;).
      </p>
      <p>
        By accessing, using, creating an account on, or booking services through the Pheefee
        platform, you agree to be bound by these Terms. If you do not agree, you may not access or
        use the platform.
      </p>

      <h2>1. Platform Purpose and Role</h2>
      <p>
        <strong>1.1 Marketplace Role.</strong> Pheefee operates an online platform that enables dog
        owners and other pet-related consumers seeking services (&ldquo;Customers&rdquo;) to locate,
        review, connect with, pay, and book remote dog-training-related services from independent
        trainers (&ldquo;Trainers&rdquo;).
      </p>
      <p>
        <strong>1.2 No Training, Veterinary, or Emergency Services Provided by Pheefee.</strong>{" "}
        Pheefee does not provide dog training services, veterinary services, medical services,
        behavioral health treatment, or emergency services. Pheefee provides only a communications,
        booking, payment, moderation, and administrative platform.
      </p>
      <p>
        <strong>1.3 Trainer and Customer Relationship.</strong> Any services booked through the
        platform are provided solely by the applicable Trainer, not by Pheefee. Trainers are
        independent providers and are not employees, agents, partners, joint venturers, or
        representatives of Pheefee.
      </p>
      <p>
        <strong>1.4 No Endorsement.</strong> Pheefee does not endorse, guarantee, or warrant any
        Trainer, training advice, service outcome, or user interaction. You are solely responsible
        for evaluating whether a Trainer and any services offered are appropriate for you and your
        dog.
      </p>

      <h2>2. Eligibility and Account Registration</h2>
      <p>
        <strong>2.1 Eligibility.</strong> To use the platform, you represent and warrant that:
      </p>
      <ol>
        <li>you are at least eighteen (18) years old;</li>
        <li>you have the legal capacity to enter into these Terms;</li>
        <li>all information you provide to Pheefee is truthful, current, and complete; and</li>
        <li>you will use the platform only for lawful purposes.</li>
      </ol>
      <p>
        <strong>2.2 Account Information.</strong> You must create and maintain an account with
        accurate and current information. You are responsible for maintaining the confidentiality of
        your account credentials and for all activity occurring under your account.
      </p>
      <p>
        <strong>2.3 One Account; No Transfers.</strong> You may not create multiple accounts for the
        same person or transfer your account to another person without Pheefee&rsquo;s written
        consent.
      </p>

      <h2>3. User Responsibilities</h2>
      <p>
        <strong>3.1 Accurate Information.</strong> Customers agree to provide complete and accurate
        information about their pet and any issue for which they seek training guidance. Customers
        must not omit or misrepresent any fact that could reasonably affect the Trainer&rsquo;s
        willingness or ability to provide services.
      </p>
      <p>
        <strong>3.2 Lawful Use.</strong> You shall comply with all applicable laws, regulations, and
        local requirements relating to your use of the platform, your dog, services provided, and
        any services booked through the platform.
      </p>
      <p>
        <strong>3.3 Dog Health and Safety.</strong> Customers are solely responsible for a
        dog&rsquo;s care, supervision, behavior, health, vaccinations, licensing, and compliance
        with applicable law. Pheefee is not responsible for any injury, damage, loss, or other harm
        caused by your dog or arising from your failure to exercise appropriate judgment and
        supervision.
      </p>

      <h2>4. Scope of Services; No Veterinary or Emergency Advice</h2>
      <p>
        <strong>4.1 Remote Services Only.</strong> The platform currently facilitates remote
        dog-training related guidance and services only. Pheefee does not currently provide or
        arrange in-person services through the platform.
      </p>
      <p>
        <strong>4.2 No Veterinary, Medical, or Emergency Advice.</strong> The platform and any
        Trainer services are not veterinary, medical, diagnostic, prescription, rehabilitation,
        mental health, or emergency services. Nothing on the platform should be understood as a
        substitute for veterinary care or emergency assistance.
      </p>
      <p>
        <strong>4.3 Emergencies.</strong> If a dog is experiencing a medical emergency, physical
        distress, aggression crisis, or other urgent safety issue, do not rely on the platform.
        Customers should immediately contact a licensed veterinarian, emergency animal hospital, or
        appropriate emergency services.
      </p>

      <h2>5. Booking, Payments, and Refunds</h2>
      <p>
        <strong>5.1 Bookings.</strong> A booking occurs when a Customer selects a Trainer, provide
        payment information, and confirm a session through the platform. Once confirmed, Customer
        agrees to pay the fees associated with that booking.
      </p>
      <p>
        <strong>5.2 Payment Authorization.</strong> By providing a payment method, Customer
        authorizes Pheefee and its payment processor to charge your payment method for all fees,
        taxes, charges, refunds, reversals, and other amounts due in connection with your use of
        the platform.
      </p>
      <p>
        <strong>5.3 Payment Processing.</strong> Pheefee may use Stripe or another third-party
        payment processor to facilitate transactions. Your use of payment services may also be
        subject to the processor&rsquo;s separate terms and policies.
      </p>
      <p>
        <strong>5.4 Refund Authority.</strong> Pheefee may review complaints and, in its discretion,
        determine whether to issue a full refund, partial refund, platform credit, service credit,
        or no refund. In making that determination, Pheefee may consider session records,
        summaries, communications, policy compliance, Trainer response, and any other information
        Pheefee deems relevant.
      </p>
      <p>
        <strong>5.5 Chargebacks and Payment Disputes.</strong> If Customer initiates a chargeback or
        payment dispute, Pheefee may investigate the matter, contest the chargeback where
        appropriate, suspend your account, and recover any associated costs to the extent permitted
        by law.
      </p>
      <p>
        <strong>5.6 No Guaranteed Results.</strong> Customers acknowledge and agree that Pheefee and
        the Trainer make no representation, warranty, or guarantee of any kind regarding any
        specific training result, behavioral outcome, improvement, or timeframe for progress. Dog
        training outcomes are inherently variable and depend on factors outside Pheefee&rsquo;s and
        the Trainer&rsquo;s control, including the dog&rsquo;s temperament, history, health,
        environment, consistency of implementation, and owner participation. Payment for a session
        or other service secures only the opportunity to receive the booked service, not any
        promised result.
      </p>

      <h2>6. Sessions, Communications, and Recordings</h2>
      <p>
        <strong>6.1 Platform Communications.</strong> You shall use only the communication tools,
        messaging channels, video functionality, and other systems designated or approved by
        Pheefee for any services arranged through the platform. You shall not use unauthorized
        off-platform communications, payment channels, or alternate systems to circumvent
        Pheefee&rsquo;s booking, monitoring, safety, recordkeeping, payment, dispute-resolution, or
        enforcement processes.
      </p>
      <p>
        <strong>6.2 Monitoring, Recording, and Transcription.</strong> Pheefee may monitor, record,
        transcribe, store, review, audit, and use platform communications, video sessions,
        messages, summaries, and related records for quality assurance, complaint review, trust and
        safety, training, policy enforcement, legal compliance, dispute resolution, product
        improvement, and other legitimate business purposes. By using the platform, you consent to
        such monitoring, recording, and transcription by Pheefee. You may not independently record,
        download, screen-record, save, copy, or otherwise create your own audio or video recordings
        of sessions or platform communications without Pheefee&rsquo;s prior written consent.
      </p>
      <p>
        <strong>6.3 Cooperation.</strong> You shall cooperate fully, promptly, and in good faith
        with any complaint review, safety review, trust-and-safety inquiry, payment dispute,
        chargeback investigation, policy enforcement matter, legal inquiry, or other investigation
        relating to your use of the platform, any booking, any session, your dog, or your
        interactions with any Trainer or other user. Such cooperation shall include, as applicable,
        timely responding to Pheefee&rsquo;s requests, providing complete and accurate information,
        furnishing requested records or supporting materials, preserving relevant evidence, and
        refraining from any conduct that interferes with or undermines Pheefee&rsquo;s review or
        resolution of the matter.
      </p>

      <h2>7. User Conduct</h2>
      <p>
        <strong>7.1 General Conduct.</strong> You shall use the platform lawfully, responsibly, and
        in strict accordance with these Terms and any applicable platform policies. You shall
        conduct yourself respectfully and professionally in all interactions on or through the
        platform and shall not engage in any conduct that is abusive, misleading, unsafe,
        disruptive, fraudulent, harassing, or otherwise harmful to Pheefee, any Trainer, any other
        user, or the integrity of the platform.
      </p>
      <p>
        <strong>7.2 Prohibited Conduct.</strong> You shall not:
      </p>
      <ol>
        <li>
          engage in harassing, threatening, abusive, discriminatory, lewd, or offensive conduct;
        </li>
        <li>use the platform for fraud, spam, deception, or any unlawful purpose;</li>
        <li>provide false account, payment, or dog-related information;</li>
        <li>
          post or transmit content that is defamatory, infringing, abusive, threatening, obscene,
          or otherwise unlawful;
        </li>
        <li>misuse recordings, communications, or platform content;</li>
        <li>interfere with the operation or security of the platform; or</li>
        <li>attempt to circumvent platform payment, booking, moderation, or safety processes.</li>
      </ol>

      <h2>8. User Content</h2>
      <p>
        <strong>8.1 User Content.</strong> You may submit, upload, transmit, post, display, or
        otherwise provide profile information, messages, images, videos, reviews, and other content
        or materials through or in connection with the platform (collectively, &ldquo;User
        Content&rdquo;). You remain solely responsible for all User Content you provide, including
        its legality, accuracy, completeness, appropriateness, and compliance with these Terms and
        applicable law. Pheefee does not assume responsibility for User Content submitted by you or
        any other user and reserves the right, but not the obligation, to review, monitor, remove,
        restrict, or refuse any User Content in its discretion.
      </p>
      <p>
        <strong>8.2 License to Pheefee.</strong> You grant Pheefee a worldwide, perpetual,
        irrevocable, sublicensable, transferable, royalty-free license to host, use, reproduce,
        store, modify, adapt, distribute, display, publish, review, create derivative works from,
        and otherwise use your User Content for platform operations, legal compliance, marketing,
        quality assurance, training, trust and safety, and related business purposes.
      </p>
      <p>
        <strong>8.3 Your Responsibility.</strong> You represent, warrant, and covenant that: (a)
        you own, control, or have obtained all rights, licenses, consents, permissions, and
        releases necessary to submit, upload, transmit, post, display, or otherwise provide the
        User Content through or in connection with the platform; (b) your User Content, and
        Pheefee&rsquo;s permitted use of such User Content as contemplated by these Terms, does not
        and will not violate any applicable law, regulation, court order, contract, duty of
        confidentiality, privacy right, publicity right, intellectual property right, or other
        third-party right; and (c) your User Content is not false, misleading, deceptive,
        defamatory, obscene, abusive, unlawful, or otherwise objectionable. You remain solely
        responsible for your User Content and for any claims, losses, or liabilities arising out of
        or relating to it.
      </p>

      <h2>9. Privacy</h2>
      <p>
        <strong>9.1 Privacy Policy.</strong> Pheefee&rsquo;s collection, use, storage, disclosure,
        and other processing of personal information is governed by its Privacy Policy, as amended
        from time to time, which is incorporated into these Terms by reference. By accessing or
        using the platform, you acknowledge that you have reviewed the Privacy Policy and
        understand that your personal information may be collected, used, stored, disclosed, and
        otherwise processed in accordance with it.
      </p>
      <p>
        <strong>9.2 Confidentiality of Other Users&rsquo; Information.</strong> In connection with
        your use of the platform, you may receive or access information relating to Trainers or
        other users, including personal, contact, scheduling, payment-related, or other non-public
        information. You shall keep such information confidential and shall not misuse, disclose,
        copy, distribute, exploit, sell, publish, or use such information for any purpose other
        than as reasonably necessary to use the platform as intended and in compliance with these
        Terms. Without limiting the foregoing, you shall not use other users&rsquo; information for
        unsolicited communications, harassment, competitive purposes, off-platform solicitation, or
        any unlawful or unauthorized purpose.
      </p>

      <h2>10. Assumption of Risk and Release</h2>
      <p>
        <strong>10.1 Assumption of Risk.</strong> You acknowledge that dog ownership, dog training,
        and reliance on remote training guidance involve inherent and sometimes unavoidable risks,
        including risks of injury to persons or animals, property damage, emotional distress,
        behavioral setbacks, ineffective or incomplete results, and other known or unknown harms.
        You further acknowledge that remote services may be limited by the accuracy and
        completeness of the information you provide, the dog&rsquo;s temperament, history, health,
        environment, and your own follow-through and supervision. You knowingly and voluntarily
        assume all risks arising out of or relating to your use of the platform, your reliance on
        Trainer advice, your participation in any session, and your interactions with Trainers,
        other users, and dogs.
      </p>
      <p>
        <strong>10.2 Release.</strong> To the fullest extent permitted by law, you hereby release,
        waive, and discharge Pheefee and its officers, directors, employees, contractors,
        affiliates, agents, successors, and assigns from any and all claims, demands, causes of
        action, damages, liabilities, losses, costs, and expenses, whether known or unknown,
        arising out of or relating to: (a) services provided or not provided by any Trainer; (b)
        disputes or interactions between you and any Trainer or other user; (c) dog behavior,
        injury, illness, death, escape, property damage, or third-party conduct; (d) your reliance
        on platform content, Trainer advice, or session materials; or (e) your access to or use of
        the platform, except to the extent caused by Pheefee&rsquo;s gross negligence, willful
        misconduct, or liability that cannot be waived under applicable law.
      </p>

      <h2>11. Indemnity</h2>
      <p>
        To the fullest extent permitted by law, you shall defend, indemnify, and hold harmless
        Pheefee and its officers, directors, employees, affiliates, contractors, agents,
        successors, and assigns from and against any and all claims, actions, demands,
        investigations, proceedings, liabilities, damages, judgments, settlements, penalties,
        fines, losses, costs, and expenses, including reasonable attorneys&rsquo; fees, arising out
        of or relating to: (a) your use of the platform; (b) your dog, including any injury,
        damage, loss, or other harm caused by your dog; (c) your breach of these Terms or any
        platform policy; (d) your violation of any law or third-party right; (e) your User Content;
        or (f) any dispute, transaction, or interaction between you and any Trainer or other user.
        Pheefee reserves the right to assume the exclusive defense and control of any matter
        subject to indemnification by you, and you shall cooperate fully with Pheefee in the
        defense of such matter.
      </p>

      <h2>12. Disclaimer of Warranties</h2>
      <p>
        The platform is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To
        the fullest extent permitted by law, Pheefee disclaims all warranties, express, implied,
        statutory, or otherwise, including any warranties of merchantability, fitness for a
        particular purpose, title, non-infringement, uninterrupted availability, security,
        accuracy, completeness, reliability, and results. Without limiting the foregoing, Pheefee
        does not warrant that the platform will be error-free, uninterrupted, secure, or free from
        delays, that any Trainer or service will meet your expectations, or that any content,
        guidance, session, or platform feature will produce any particular result or outcome.
      </p>

      <h2>13. Limitation of Liability</h2>
      <p>
        <strong>13.1 Excluded Damages.</strong> To the fullest extent permitted by law, Pheefee
        shall not be liable for any indirect, incidental, special, consequential, exemplary, or
        punitive damages, or for any loss of profits, revenue, business opportunity, goodwill,
        data, use, or expected savings, arising out of or relating to these Terms, the platform,
        any Trainer service, any user interaction, or any dog-related incident, even if advised of
        the possibility of such damages.
      </p>
      <p>
        <strong>13.2 Liability Cap.</strong> To the fullest extent permitted by law,
        Pheefee&rsquo;s aggregate liability arising out of or relating to these Terms, the
        platform, or any related claim shall not exceed the total amount of fees actually paid by
        you to Pheefee during the twelve (12) months immediately preceding the event giving rise to
        the claim.
      </p>
      <p>
        <strong>13.3 Non-Excludable Liability.</strong> Nothing in these Terms excludes or limits
        liability to the extent such liability cannot be excluded or limited under applicable law.
      </p>

      <h2>14. Suspension and Termination</h2>
      <p>
        <strong>14.1 Pheefee Rights.</strong> Pheefee may suspend, restrict, deactivate, or
        terminate your account or access to the platform immediately, with or without notice, if
        Pheefee determines, in its sole discretion, that:
      </p>
      <ol>
        <li>you breached these Terms or any platform policy;</li>
        <li>you engaged in unsafe, abusive, unlawful, fraudulent, or misleading conduct;</li>
        <li>you created legal, payment, reputational, safety, or operational risk;</li>
        <li>you misused the platform, a Trainer relationship, or payment systems; or</li>
        <li>your continued use of the platform no longer serves Pheefee&rsquo;s business interests.</li>
      </ol>
      <p>
        <strong>14.2 Effect of Termination.</strong> Upon suspension or termination, Pheefee may
        remove your account, disable or restrict access to the platform, cancel pending or future
        bookings, retain and preserve platform records, communications, and account information,
        withhold or offset credits or refunds to the extent permitted by law, and take any other
        reasonable steps necessary to protect the platform, its users, Trainers, and Pheefee&rsquo;s
        business interests.
      </p>

      <h2>15. Dispute Resolution</h2>
      <p>
        <strong>15.1 Governing Law.</strong> These Terms and any dispute arising out of or relating
        to them shall be governed by the laws of the State of Tennessee, without regard to
        conflict-of-law principles.
      </p>
      <p>
        <strong>15.2 Informal Resolution.</strong> Before filing any formal claim, the parties
        shall first attempt in good faith to resolve the dispute informally by written notice.
      </p>
      <p>
        <strong>15.3 Binding Arbitration.</strong> Except for claims eligible for small claims
        court or claims seeking temporary or preliminary injunctive relief, any dispute arising out
        of or relating to these Terms, the platform, or the relationship between the parties shall
        be resolved by final and binding arbitration administered by the American Arbitration
        Association in accordance with its applicable rules.
      </p>
      <p>
        <strong>15.4 Venue.</strong> The arbitration shall take place in Davidson County,
        Tennessee, unless the parties agree otherwise or the applicable arbitration rules require a
        remote proceeding.
      </p>
      <p>
        <strong>15.5 Waiver of Jury Trial and Class Claims.</strong> To the fullest extent
        permitted by law, each party waives the right to trial by jury and the right to participate
        in any class, collective, coordinated, consolidated, or representative action or
        proceeding.
      </p>
      <p>
        <strong>15.6 Costs and Fees.</strong> Each party shall bear its own attorneys&rsquo; fees
        and costs except to the extent otherwise awarded by the arbitrator or required by
        applicable law. Arbitration fees shall be allocated in accordance with the applicable
        arbitration rules, except as otherwise required by law or reallocated by the arbitrator in
        the final award.
      </p>
      <p>
        <strong>15.7 Equitable Relief.</strong> Nothing in this Section prevents Pheefee from
        seeking injunctive or equitable relief in a court of competent jurisdiction for misuse of
        the platform, intellectual property infringement, unauthorized disclosure of confidential
        information, or threatened harm to the platform or its users.
      </p>

      <h2>16. General Terms</h2>
      <p>
        <strong>16.1 Changes to Terms.</strong> Pheefee may update these Terms from time to time.
        Updated Terms become effective upon posting or upon other notice given by Pheefee. Your
        continued use of the platform after the effective date of updated Terms constitutes
        acceptance.
      </p>
      <p>
        <strong>16.2 Electronic Consent.</strong> You agree that Pheefee may provide notices,
        disclosures, and other communications electronically and that your electronic acceptance of
        these Terms carries the same force as a handwritten signature.
      </p>
      <p>
        <strong>16.3 Assignment.</strong> You may not assign, transfer, delegate, or sublicense any
        rights or obligations under these Terms without Pheefee&rsquo;s prior written consent.
        Pheefee may assign these Terms in connection with a merger, sale, restructuring, or other
        corporate transaction.
      </p>
      <p>
        <strong>16.4 Severability.</strong> If any provision of these Terms proves unenforceable,
        the remaining provisions will remain in full force to the maximum extent permitted by law.
      </p>
      <p>
        <strong>16.5 Waiver.</strong> A failure or delay to enforce any provision of these Terms
        does not waive that provision or any other provision.
      </p>
      <p>
        <strong>16.6 Entire Agreement.</strong> These Terms, together with all documents and
        policies incorporated by reference, constitute the entire agreement between you and Pheefee
        regarding the subject matter hereof and supersede all prior or contemporaneous
        understandings on that subject.
      </p>
      <p>
        <strong>16.7 Notices.</strong> Legal notices to Pheefee must be sent to:
      </p>
      <p>
        <strong>Pheefee Technologies Inc.</strong>
        <br />
        <a href="mailto:pheefeeplatform@gmail.com">pheefeeplatform@gmail.com</a>
      </p>
      <p>
        Notices to you may be sent to the email address or other contact information associated
        with your account.
      </p>
  </>
);

export default CustomerTermsContent;
